/**
 * DayHeader - La cabecera del día.
 *
 * Antes eran tres bloques con marco: el resumen del día, una tarjeta con la fecha y el
 * tipo de día, y otra con tres desplegables siempre desplegados. Tres cajas para decir
 * cuatro cosas, y las tres compitiendo por la atención antes de llegar a las comidas,
 * que es a lo que se viene.
 *
 * Ahora es una sola zona sin marcos, separada por líneas finas:
 *   - la fecha y el tipo de día en una línea,
 *   - la configuración resumida en texto ("4 comidas · tras comida 2 · intra + post"),
 *     que se despliega solo cuando se quiere cambiar algo,
 *   - los tres macros del día en filas con su nombre completo,
 *   - el peri y el detalle por comida, en pequeño.
 */
import { tamanoDelNumero } from '../../lib/tamanoDelNumero';
import React from 'react';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import ConfigSection from './ConfigSection';
import { DayDetailTable, StatusDot } from './DaySummary';
import { leerMacro, claseDelMacro, fondoDelMacro, llevaPunto } from '../../lib/estadoDelMacro';
import { num1, alDecima } from '../../lib/numeros';


/** «Las cuatro comidas», con el número escrito y el que de verdad tiene ese día.
 *
 *  El artefacto lo escribe así, con la palabra y no con la cifra. Se pone el número real
 *  porque no todo el mundo hace cuatro: decir «las cuatro» a quien hace tres es mentirle en
 *  la frase que le dice que ha terminado. Sin número, «Las comidas», que también es cierto.
 */
const NUMERO_EN_LETRA = { 1: 'La comida', 2: 'Las dos comidas', 3: 'Las tres comidas',
                          4: 'Las cuatro comidas', 5: 'Las cinco comidas', 6: 'Las seis comidas' };
const cuantasComidas = (n) => NUMERO_EN_LETRA[Number(n)] || 'Las comidas';

const DayHeader = ({
    // su dieta no lleva perientreno (ver ConfigSection)
    sinPeri = false,
    // fecha
    currentDate, formatDate, changeDate, setCalendarOpen, diaSinMarcar,
    // tipo de día y configuración
    tipoDia, handleSetTipoDia,
    numComidas, setNumComidas, momentoEntreno, setMomentoEntreno, opcionPeri, setOpcionPeri, singleMeal,
    configExpanded, setConfigExpanded,
    // macros
    dayMacros, dayTarget, servedPeriP, servedPeriH, servedPeriG = 0, totalPeriP, totalPeriH,
    // detalle
    summaryExpanded, setSummaryExpanded,
    mealOrder, mealInfo, calculateMealMacros, getMealStatus,
    // el estado del día, calculado fuera y en un solo sitio (ver NutritionPage)
    estadoDelDia, loQueFaltaAlDia, onCrearDia, onTerminarDia, terminando,
}) => {
    // La parte de las COMIDAS: al día se le quita el peri, que lleva su propia cuenta
    // (el chip de abajo y las filas del detalle). De aquí sale el ESTADO del día.
    const mainP = dayMacros.P - servedPeriP;
    const mainH = dayMacros.H - servedPeriH;
    const mainG = dayMacros.G - servedPeriG;
    // Objetivo de las COMIDAS = total del día menos el peri que se cuenta aparte. No vale
    // usar P_entreno: en `sin_peri` (y en `solo_intra`) parte del presupuesto de perientreno
    // se reparte ENTRE LAS COMIDAS, así que los objetivos por comida suman más que los macros
    // de entreno. La cabecera pedía de menos justo ese peri repartido (30 P y 16 H en el caso
    // que lo destapó): con las cuatro comidas cuadradas el día decía "te pasas", y como nunca
    // quedaba UNA sola comida sin cuadrar tampoco aparecía "Volcar macros aquí".
    const tgtP = (dayTarget.P_total ?? 0) - (totalPeriP || 0);
    const tgtH = (dayTarget.H_total ?? 0) - (totalPeriH || 0);
    const tgtG = dayTarget.G_total ?? 0;   // el objetivo del peri no lleva grasa

    // LOS NÚMEROS GRANDES ENSEÑAN EL DÍA ENTERO, PERI INCLUIDO (doc 21-08, apartado 11):
    // arriba un solo objetivo y un solo servido, con el perientreno dentro, en los cuatro
    // modos de peri. El reparto interno no cambia: `val`/`tgt` siguen siendo los de las
    // comidas y de ahí sale el estado (el mismo criterio que getDayStatus); `valDia`/`tgtDia`
    // son solo lo que se pinta. El chip del perientreno sigue debajo con su propia cuenta.
    //
    // LA GRASA VA SIN EL PERI, Y ESE ERA EL DESCUADRE DEL PUNTO 178 (27-08): «el mismo día,
    // la grasa dice 40 aquí y 41 en Nutrición».
    // Aquí ponía `valDia: dayMacros.G`, que SÍ lleva la grasa del intra y del post, y lo
    // comparaba contra `tgtDia: tgtG`, que NO la lleva (en el método el objetivo del peri no
    // tiene grasa). Creado y objetivo medían cosas distintas en la misma columna. Y el Inicio
    // no tenía el fallo: allí lo montado sale de `servido_comidas`, que el servidor cuenta
    // sin el peri. Dos pantallas, dos números, y el bueno era el del Inicio.
    // La línea de arriba decía «en grasa día y comidas coinciden: el peri no lleva grasa»:
    // es una suposición, y en producción hay 27 días guardados que la desmienten (batidos
    // con 2 a 4 g de grasa en el intra o el post). `mainG` ya era el número bueno -- de él
    // sale el estado del día -- y solo se pintaba el otro.
    // LAS COMIDAS QUE TODAVÍA NO EXISTEN (12-09).
    //
    // El artefacto da una sola frase para el día a medias: «cada comida está dentro de su
    // margen, pero sumadas se quedan cortas». Es verdad en el caso que él dibuja -- las
    // cuatro comidas creadas y el total corto --, y es MENTIRA en el día que tiene una
    // comida sin crear: esa comida no está dentro de ningún margen, es que no está.
    //
    // Y se nota en el botón de al lado: «Terminar el día» reparte entre las comidas lo que
    // le falta al total, pero en una comida vacía no hay nada que repartir, y proponer
    // alimentos no lo hace ([[cuadrar-no-propone-alimentos]]). Así que el día se quedaba
    // corto, el cliente volvía a pulsar y volvía a pasar lo mismo, con la banda diciéndole
    // que todas sus comidas estaban bien.
    const sinCrear = (mealOrder || [])
        .filter((k) => k !== 'Intra' && k !== 'Post')
        .filter((k) => getMealStatus && getMealStatus(k) === 'empty');

    // Y LO MISMO CON EL DÍA LISTO (13-09). Su frase es «las cuatro comidas y el total del
    // día. No te falta nada», y en su maqueta es verdad porque las cuatro están cuadradas.
    // Pero el día cierra por el TOTAL -- «la comida se da por válida con margen; el día no:
    // el día tiene que cerrar», su propia regla de fondo --, así que se puede llegar a «Día
    // listo» con una comida fuera de su margen: pasó en la pantalla del sábado 12, con el
    // día en verde y la comida 1 a 4,4 g de grasa.
    //
    // Ahí la frase promete dos cosas y una es falsa. Su literal se queda donde es verdad, y
    // cuando alguna comida se sale se dice solo lo que se sostiene: el total.
    const fueraDeMargen = (mealOrder || [])
        .filter((k) => getMealStatus && !['empty', 'cuadrada'].includes(getMealStatus(k)));
    // Y «DÍA LISTO» SOLO SI LAS COMIDAS TAMBIÉN LO ESTÁN (Francisco, 22-09: «me dice día
    // listo y lo mismo tengo la opción de cuadrar: se supone que está listo, cosa que no es
    // así»). El total puede cerrar porque el exceso de una comida tapa la falta de otra; eso
    // no es un día listo, es un día a medias, y la banda lo dice así, en naranja y con el
    // nombre de cada comida. El verde se reserva para cuando no queda nada que hacer.
    // ...Y SE DESHACE EL 25-09 (Jesús, reunión: «si el total del día cuadra, da igual; no
    // tiene que poner ningún mensaje. La prioridad es que cuadre el día: si el día está
    // cuadrado, todo vale»). Manda lo más reciente: con el total cuadrado es «Día listo»,
    // aunque alguna comida vaya por su lado, y no hay botón de «Cuadrar las comidas».
    const listoAMedias = false;
    const nombreDe = (k) => mealInfo?.[k]?.name || k;
    const queLePasa = (k) => {
        const s = getMealStatus ? getMealStatus(k) : null;
        return s === 'sobra' ? 'se pasa' : s === 'falta' ? 'se queda corta' : 'se sale de su margen';
    };
    const comidasAMedias = fueraDeMargen.map((k) => `la ${nombreDe(k)} ${queLePasa(k)}`);

    const macros = [
        { key: 'P', label: 'Proteína', val: mainP, tgt: tgtP || 0, valDia: dayMacros.P, tgtDia: dayTarget.P_total ?? 0 },
        { key: 'H', label: 'Hidratos', val: mainH, tgt: tgtH || 0, valDia: dayMacros.H, tgtDia: dayTarget.H_total ?? 0 },
        { key: 'G', label: 'Grasa', val: mainG, tgt: tgtG || 0, valDia: mainG, tgtDia: tgtG || 0 },
    ];

    return (
        <section data-testid="day-summary" className="mt-4">
            {/* Fecha, tipo de día y configuración resumida */}
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
                {/* `flex-wrap`, y no es cosmética: sin ella esta fila no parte nunca, y el
                    aviso de «¿Este día entrenas o descansas?» -- que lleva `w-full` para
                    caer a su línea -- se salía por la derecha de la pantalla en 390 px. Se
                    leía «¿Este día entrenas o descansa... Tus macro... cambian.», cortado.
                    Justo el aviso que existe para que no se coma 60 g de hidratos de más. */}
                <div className="flex flex-wrap items-center gap-2 min-w-0" data-testid="dia-y-tipo">
                    {/* LAS FLECHAS Y LA FECHA, JUNTAS (26-09): a 290 px se separaban -- «<» sola arriba,
                        la fecha en medio y «>» junto a Entreno --. Van en un grupo que no se parte; si
                        no cabe, lo que baja es Entreno/Descanso entero. */}
                    <div className="flex items-center gap-1 min-w-0 max-w-full">
                    <button onClick={() => changeDate(-1)} aria-label="Día anterior"
                        className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-brand hover:bg-brand/10 transition-colors flex-shrink-0">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button onClick={() => setCalendarOpen(true)} data-testid="open-calendar-btn"
                        className="flex items-center gap-2 min-w-0 h-9 px-2 rounded-xl hover:bg-muted/60 transition-colors">
                        <Calendar className="w-4 h-4 text-brand flex-shrink-0" />
                        {/* Sin `capitalize`: desde la parte 6 la fecha es «Jueves, 27 de
                            agosto» y esa clase pone mayúscula en CADA palabra («27 De
                            Agosto»). La primera letra ya la pone `formatDate`, que es la
                            única que va en mayúscula. */}
                        <span className="font-heading font-bold text-lg text-foreground truncate">{formatDate(currentDate)}</span>
                    </button>
                    <button onClick={() => changeDate(1)} aria-label="Día siguiente"
                        className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-brand hover:bg-brand/10 transition-colors flex-shrink-0">
                        <ChevronRight className="w-5 h-5" />
                    </button>
                    </div>

                    {/* Si nadie ha dicho qué día es, el selector se marca (punto 4.17): la app
                        abre en «Entreno» porque hay que abrir en algo, pero en un día de
                        descanso eso son 60 g de hidratos y 45 de perientreno de más. Medido en
                        producción: de 14.027 días guardados, 2 dicen descanso. */}
                    <div className={`inline-flex rounded-xl bg-muted p-0.5 ml-1 flex-shrink-0 ${diaSinMarcar ? 'ring-2 ring-brand ring-offset-2 ring-offset-background' : ''}`}>
                        <button data-testid="tipo-dia-entrenamiento" onClick={() => handleSetTipoDia('entrenamiento')}
                            className={`px-3 h-8 rounded-lg text-xs font-bold transition-colors ${tipoDia === 'entrenamiento' ? 'bg-brand text-white' : 'text-muted-foreground hover:text-foreground'}`}>
                            Entreno
                        </button>
                        <button data-testid="tipo-dia-descanso" onClick={() => handleSetTipoDia('descanso')}
                            className={`px-3 h-8 rounded-lg text-xs font-bold transition-colors ${tipoDia === 'descanso' ? 'bg-brand text-white' : 'text-muted-foreground hover:text-foreground'}`}>
                            Descanso
                        </button>
                    </div>
                    {/* La frase «¿Este día entrenas o descansas?» se quitó (punto 8 del doc
                        del 23-08: fuera, y nada en su lugar). El aviso de día sin marcar
                        sigue siendo el aro naranja del conmutador, en el sitio donde se
                        actúa (punto 4.17). */}

                    {/* AQUÍ IBAN LOS DISTINTIVOS «CUADRADO» Y «TE PASAS», y se han ido con
                        el titular (Francisco, 26-08). Existían porque el titular era solo de
                        móvil y en escritorio había que decir el estado del día de alguna
                        manera. Ahora lo dicen los tres puntos y las tres palabras, en los dos
                        tamaños, así que esto era el mismo dato otra vez y con otro rojo. */}
                </div>

                {/* El resumen de la configuración se ha ido al «···» de la cabecera
                    (punto 113: «se queda, dentro del ···»). Estaba aquí en escritorio y en
                    otro botón gemelo debajo de los números en el móvil: dos sitios para el
                    mismo botón, y ninguno de los dos era donde están las demás acciones. */}
            </div>

            {/* LO QUE LE QUEDA POR COMER, no lo que lleva (documento del 10-08, pantallas
                8, 9 y 10). «Es la pregunta real a las diez de la noche. Nadie abre la app
                para saber lo que ya se ha comido.»

                Aquí había tres barras con «120 / 190», que obligan a restar de cabeza tres
                veces para saber lo único que hace falta saber. Ahora el número grande es lo
                que falta, y debajo, pequeño, sigue estando de cuánto era el total, que es lo
                que da la referencia.

                Tres estados, con su titular, que es lo que el documento echaba en falta:
                la app no distinguía «sin empezar» de «a medias» ni de «terminado». */}
            {(() => {
                return (
                    <div className="mt-5" data-testid="dia-resumen">
                        {/* UN SOLO NÚMERO Y SIEMPRE EL MISMO: LO QUE LLEVAS CREADO
                            (puntos 105 a 107 del artifact del 25-08).

                            «Inicio es cómo vas. Nutrición es lo que llevas creado. Cada
                            pantalla enseña un solo número y siempre el mismo.»

                            Hasta hoy este bloque cambiaba de criterio SOLO, y de dos maneras
                            a la vez. El titular rotaba entre cuatro frases -- «Día cuadrado»,
                            «Hoy tienes que comer», «Te has pasado», «Te queda por comer hoy»
                            -- y, peor, EL NÚMERO cambiaba con él: era lo que FALTA mientras
                            ibas a medias y pasaba a ser lo SERVIDO al cuadrar o al pasarte.
                            El mismo hueco, dos magnitudes distintas, y nadie te decía cuál
                            estabas mirando. Y en escritorio no era ninguna de las dos: eran
                            tres barras de 1 px con «Te faltan N de X», un tercer criterio.

                            Ahora es siempre lo creado, en los dos tamaños, y el estado lo
                            dicen el punto, la palabra y la barra, con el mismo motor y el
                            mismo margen de 4 g que el Inicio (lib/estadoDelMacro): «el color,
                            el margen de 4 y las palabras son los mismos de la parte 2». */}
                        {/* LA BANDA, ANTES DE LOS NÚMEROS (Jesús, «Cuando la dieta está
                            lista», 9-09). Su frase: «se ve antes de leer un solo número. Es
                            el estado que hoy no existe en ninguna pantalla».

                            El color va en la banda y en la palabra; LOS NÚMEROS SIGUEN EN
                            BLANCO, que es lo que él pide expresamente y lo mismo que decidió
                            para Mi semana. */}
                        {/* DOS CASOS SIN FRANJA (Jesús, reunión del 25-09):
                            - el día sin crear: «aquí sobra el día sin crear de arriba» y su
                              círculo; ya lo dice el bloque de abajo, con su botón;
                            - el día que se está montando (alguna comida sin crear): «estoy
                              montando una comida y me sale arriba este aviso; si estoy haciendo
                              la dieta no tiene sentido que salga». */}
                        {estadoDelDia && estadoDelDia !== 'sin_crear'
                            && !(estadoDelDia === 'sin_terminar' && sinCrear.length > 0) && (
                            <div data-testid={`dia-banda-${estadoDelDia}`}
                                /* LOS COLORES DEL ARTEFACTO (Francisco, 12-09: «los textos
                                   tienen que ser los del artefacto»).
                                   El 11-09 se quitó el color de «sin terminar» por una
                                   confusión mía: lo que había era ÁMBAR, y su regla dice que
                                   el naranja se reserva para lo que se pasa. Pero el
                                   artefacto pide ESE naranja para esta banda, no el ámbar, y
                                   eso no choca con la regla: aquí el naranja no es una riña,
                                   es la marca de la casa llamando la atención sobre lo que
                                   queda por hacer. Verde para el día listo, naranja para el
                                   que falta, gris para el que no ha empezado. */
                                className={`w-full max-w-md rounded-xl px-3 py-2 mb-3 flex items-center justify-between gap-3 border ${
                                    estadoDelDia === 'listo' && !listoAMedias ? 'border-emerald-500/40 bg-emerald-500/5'
                                        : estadoDelDia === 'sin_terminar' || listoAMedias ? 'border-brand/45 bg-brand/[0.09]'
                                            : 'border-border bg-muted/30'}`}>
                                <div className="min-w-0 text-left">
                                    <p className={`text-sm font-bold ${
                                        estadoDelDia === 'listo' && !listoAMedias ? 'text-emerald-400'
                                            : estadoDelDia === 'sin_terminar' || listoAMedias ? 'text-brand'
                                                : 'text-foreground/55'}`}>
                                        {listoAMedias ? '⚠ Te falta cuadrar las comidas'
                                            : estadoDelDia === 'listo' ? '✓ Día listo'
                                                : estadoDelDia === 'sin_terminar' ? '⚠ Te falta terminar el día'
                                                : 'Día sin crear'}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {/* SUS FRASES, LAS DEL ARTEFACTO. La del día listo
                                            dice «las cuatro comidas», y el número se pone
                                            de verdad: con tres comidas, «las tres». Lo único
                                            que no es literal es «montado», que él quiere
                                            fuera de la app en todas partes. */}
                                        {estadoDelDia === 'listo'
                                            ? (fueraDeMargen.length === 0
                                                ? `${cuantasComidas(numComidas)} y el total del día. No te falta nada.`
                                                : 'El total del día cuadra. No te falta nada.')
                                            : estadoDelDia === 'sin_terminar'
                                                /* «TE FALTAN», Y NO EL NÚMERO A SECAS (Jesús,
                                                   vídeo del 20-09): «17,4 gramos de proteína,
                                                   no entiendo qué quiere decir eso, si falta,
                                                   si sobra o qué». El titular dice que falta
                                                   terminar el día, pero la cifra sola no dice
                                                   de qué lado está. */
                                                ? [loQueFaltaAlDia ? `Te faltan ${loQueFaltaAlDia}.` : null,
                                                    /* Y LO DE LAS COMIDAS SE COMPRUEBA ANTES DE
                                                       DECIRLO (el mismo vídeo). Esta frase salía
                                                       con que no faltara ninguna comida POR
                                                       CREAR, sin mirar si alguna estaba fuera de
                                                       su margen, así que la pantalla afirmaba
                                                       que todas estaban bien mientras justo
                                                       debajo una decía «sobran 28 de hidratos».
                                                       Reproducido el 20-09 con las cuatro
                                                       comidas fuera y la banda diciendo que
                                                       ninguna lo estaba.
                                                       `fueraDeMargen` ya existía aquí al lado:
                                                       lo usaba la rama del día listo y a ésta
                                                       nadie se lo había pasado. */
                                                    sinCrear.length > 0
                                                        ? (sinCrear.length === 1
                                                            ? `Todavía no has creado la ${mealInfo?.[sinCrear[0]]?.name || 'comida'}.`
                                                            : `Todavía te faltan ${sinCrear.length} comidas por crear.`)
                                                        : fueraDeMargen.length === 0
                                                            ? 'Cada comida está dentro de su margen, pero sumadas se quedan cortas.'
                                                            : fueraDeMargen.length === 1
                                                                ? `La ${mealInfo?.[fueraDeMargen[0]]?.name || 'comida'} también se sale de su margen.`
                                                                : `Y ${fueraDeMargen.length} comidas se salen de su margen.`]
                                                    .filter(Boolean).join(' ')
                                                : 'Todavía no has creado ninguna comida de este día.'}
                                    </p>
                                </div>
                                {/* EL BOTÓN DE TERMINAR, A NIVEL DE DÍA. Dentro de cada comida
                                    ya existía «Cuadrar»; faltaba el mismo para el día, «que
                                    reparta entre las comidas lo que le falte al total». Es el
                                    mismo motor que ya usa el aviso de «no cuadra»
                                    (`refit-diet`), que ajusta hacia arriba y hacia abajo. */}
                                {/* Y CUANDO EL DÍA CIERRA PERO LAS COMIDAS NO (Francisco, 21-09).
                                    «Dice día listo, pero me voy a la Comida 1 y le faltan
                                    hidratos, y a la 4 le sobran.» Pasa porque el exceso de una
                                    tapa la falta de otra y el total sale bien. La banda ya lo
                                    contaba en la línea de abajo, pero no había nada que pulsar:
                                    se le enseñaba el problema y se le dejaba con él. Es el mismo
                                    botón y el mismo motor, que ahora también cuadra las comidas
                                    una por una y avisa si para eso hay que quitar algo. */}
                                {false && estadoDelDia === 'listo' && fueraDeMargen.length > 0 && onTerminarDia && (
                                    <button onClick={onTerminarDia} disabled={terminando}
                                        data-testid="boton-cuadrar-las-comidas"
                                        className="shrink-0 rounded-xl font-bold text-xs px-3 py-2 border border-emerald-500/50
                                                   text-emerald-400 hover:bg-emerald-500/10 transition-colors disabled:opacity-50">
                                        {terminando ? 'Cuadrando...' : 'Cuadrar las comidas'}
                                    </button>
                                )}
                                {estadoDelDia === 'sin_terminar' && onTerminarDia && (
                                    <button onClick={onTerminarDia} disabled={terminando}
                                        data-testid="boton-terminar-dia"
                                        /* MACIZO, COMO EN EL ARTEFACTO (12-09). De contorno se
                                           leía como un enlace más, y es la acción de la banda:
                                           la única cosa que hay que tocar ahí. La letra va
                                           oscura porque sobre el naranja de la casa el blanco
                                           no llega al contraste que pide el resto de la app. */
                                        className="shrink-0 rounded-xl font-bold text-xs px-3 py-2 bg-brand text-[#141414] hover:bg-brand/85 transition-colors disabled:opacity-50">
                                        {terminando ? 'Terminando...' : 'Terminar el día'}
                                    </button>
                                )}
                                {estadoDelDia === 'sin_crear' && onCrearDia && (
                                    <button onClick={onCrearDia} data-testid="boton-crear-dia-banda"
                                        /* GRIS MACIZO, NO NARANJA (12-09). El día sin crear es
                                           un estado en reposo, no un aviso: la banda entera va
                                           en gris y el botón no tiene por qué gritar más que
                                           ella. El naranja se queda para el día a medias, que
                                           sí pide que hagas algo. */
                                        className="shrink-0 rounded-xl font-bold text-xs px-3 py-2 bg-muted text-foreground hover:bg-muted/70 transition-colors">
                                        Crear el día
                                    </button>
                                )}
                            </div>
                        )}
                        <div className="grid grid-cols-3 gap-3 max-w-md" data-testid="dia-numeros">
                            {/* Los tres del mismo tamaño: el que pide el más largo (26-09). */}
                            {(() => { const masLargo = macros.map((m) => num1(alDecima(m.valDia))).sort((x, y) => y.length - x.length)[0]; return macros.map(({ key, label, valDia, tgtDia }) => {
                                // CON SU DECIMAL, Y LA PALABRA CONTADA CONTRA ÉL (Francisco,
                                // 3-09). El número iba redondeado a entero y la palabra se
                                // calculaba con el valor exacto: se leía «47» y debajo «faltan
                                // 188,5». Los dos a la décima, como en el resto de la app.
                                const creado = alDecima(valDia);
                                const meta = alDecima(tgtDia);
                                const lectura = leerMacro({ vista: 'dieta', hay: creado, objetivo: meta });
                                return (
                                    // Centrado dentro de su columna, como en Inicio: los tres
                                    // números tienen anchos distintos (235, 60) y alineados a
                                    // la izquierda el bloque se ve descolgado.
                                    <div key={key} className="text-center" data-testid={`dia-${key}`}>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center justify-center gap-1">
                                            {label}
                                            {llevaPunto(lectura.color) && (
                                                <span data-testid={`dia-punto-${key}`}
                                                    className={`w-1.5 h-1.5 rounded-full ${fondoDelMacro(lectura.color)}`} />
                                            )}
                                        </p>
                                        {/* 44 PX, LOS MISMOS QUE INICIO (punto 107). Estaban a
                                            34 en el móvil y 40 en el ordenador, y el 172 subió
                                            los de Inicio a 44 sin tocar estos: dos pantallas
                                            que dicen lo mismo -- los tres macros del día -- y
                                            lo decían con dos tamaños, así que al lado del
                                            Inicio estos se veían pequeños.
                                            El 107 pedía «mismo tamaño y mismo estirado»; el
                                            estirado ya lo daba `.numero-grande`, faltaba el
                                            tamaño. Sin `sm:`: en Inicio tampoco lo hay, y era
                                            justo el escalón el que metía el tercer número. */}
                                        {/* EL DÍA SIN CREAR NO SE RIÑE (artefacto, 12-09). Con
                                            el día vacío los tres números decían «faltan 300,1»,
                                            «faltan 160,1», «faltan 50,1» en blanco: tres avisos
                                            de algo que todavía no ha pasado. El artefacto lo
                                            deja en «de 160», que es la meta y nada más. El «0»
                                            se queda porque es verdad: no llevas nada, y se
                                            queda EN BLANCO porque el artefacto lo dice con
                                            todas las letras: «el color va en la banda y en la
                                            palabra; los números siguen en blanco». */}
                                        {/* Y SI NO CABEN, MÁS PEQUEÑOS (Francisco, 26-09, en un Android estrecho:
                                            «192,9178,459,3 — pensaba que eran 9.000 de hidratos»). A 44 px
                                            «192,9» mide unos 130 y la columna del móvil unos 100: los tres
                                            se pegaban. La misma regla que Inicio (`tamanoDelNumero`). */}
                                        <p className={`numero-grande font-data leading-none mt-1.5 text-foreground ${tamanoDelNumero(masLargo)}`}>
                                            {num1(creado)}
                                        </p>
                                        <p data-testid={`dia-palabra-${key}`}
                                            className={`text-xs mt-1 ${
                                                estadoDelDia === 'sin_crear'
                                                    ? 'text-muted-foreground' : claseDelMacro(lectura.color)}`}>
                                            {estadoDelDia === 'sin_crear' ? `de ${num1(meta)}` : lectura.palabra}
                                        </p>
                                        {lectura.barra && (
                                            <div className="h-1 rounded-full bg-muted mt-1.5 overflow-hidden">
                                                <div data-testid={`dia-barra-${key}`}
                                                    className={`h-full rounded-full ${fondoDelMacro(lectura.barra.color)}`}
                                                    style={{ width: `${lectura.barra.largo}%` }} />
                                            </div>
                                        )}
                                    </div>
                                );
                            }); })()}
                        </div>
                        <p className="text-xs text-muted-foreground mt-3 max-w-md text-center"
                            data-testid="dia-pie">
                            Lo que llevas creado
                        </p>
                        {/* AQUÍ IBA «YA TIENES CUBIERTOS LOS HIDRATOS Y LA GRASA», y se ha
                            ido (punto 109): «los dos números ya salen en verde y con cuadrado
                            debajo. Es decirlo dos veces».

                            Nació cuando arriba solo se decía lo que falta y no lo que ya está
                            («saber lo que ya está cubierto evita ponerse a buscar proteína
                            cuando lo que falta son hidratos», Jesús, 11-08). Desde que cada
                            macro lleva su punto y su palabra, eso ya está dicho donde se mira. */}

                    </div>
                );
            })()}

            {/* AQUÍ IBAN LAS TRES BARRAS DE ESCRITORIO, y se han ido (puntos 105 a 107).
                Decían «Te faltan 94 de 120», o sea LO QUE FALTA, mientras los números del
                teléfono decían unas veces lo que falta y otras lo servido: tres criterios
                para el mismo dato en la misma pantalla, según el tamaño de la ventana y
                según cómo fuera el día. Ahora el bloque de números de arriba es el mismo en
                los dos tamaños y dice siempre lo mismo: lo que llevas creado. */}

            {/* OBJETIVOS PROVISIONALES (tarea 1.4). Los macros del día se calcularon con un
                perfil con huecos o con datos imposibles (edad 5, estatura de 1 cm de la
                importación de Calma): no se recalcula nada ni se cambia ningún número, solo
                se rotula y se empuja a la pantalla de completar que ya existe. Rótulo
                discreto, no un aviso: los objetivos siguen siendo los suyos. */}
            {/* AQUÍ IBA «Macros provisionales · nos falta tu altura y revisa tu edad...»,
                y se ha ido a Mi perfil (punto 111): «es un problema de ficha metido en la
                pantalla de comer, y ocupa tres líneas». En el menú queda un punto naranja
                (ClientDashboard, `fichaPendiente`) para que se vea que hay algo pendiente
                sin tener que entrar a mirar. Los números que enseña esta pantalla siguen
                siendo los suyos: lo que hacía falta era arreglarlos donde se arreglan. */}

            {/* La configuración desplegada. Ahora la abre el «···» de la cabecera (punto
                113), así que necesita su propia forma de cerrarse: antes se cerraba con el
                mismo botón que la abría, y ese botón ya no está aquí. */}
            {/* LA LÍNEA DE CONFIGURACIÓN («4 comidas · entreno tras la comida 1 · intra +
                post · Cambiar») SE RETIRA (Francisco, 22-09: «oculta, ya está en el menú de
                los tres puntos para abrirlo si se quiere»). Se había puesto el 21-09 por la
                incidencia 5 de JDG en Trello; manda lo más reciente. El «···» sigue abriendo
                la configuración con «Cambiar la configuración del día». */}
            {configExpanded && (
                <div className="mt-3 rounded-xl border border-border bg-muted/30 p-3" data-testid="config-section">
                    <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                        <ConfigSection
                            inline
                            tipoDia={tipoDia}
                            momentoEntreno={momentoEntreno}
                            setMomentoEntreno={setMomentoEntreno}
                            opcionPeri={opcionPeri}
                            setOpcionPeri={setOpcionPeri}
                            numComidas={numComidas}
                            setNumComidas={setNumComidas}
                            singleMeal={singleMeal}
                            sinPeri={sinPeri}
                        />
                    </div>
                    {/* «EL LISTO ES MUY PEQUEÑO Y NO PARECE UN BOTÓN» (César, Trello 21-09) y
                        «está ahí pero no llama la atención» (Francisco, 21-09). Se queda donde
                        está, debajo de los selectores, pero como el botón principal que es: a
                        todo lo ancho y alto. Y al abrir el bloque la pantalla baja
                        hasta él, que en el teléfono caía justo detrás de la barra de abajo. */}
                    <button onClick={() => setConfigExpanded(false)} data-testid="cerrar-config"
                        ref={(el) => { if (el && !el.dataset.visto) { el.dataset.visto = '1'; setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50); } }}
                        className="mt-4 w-full h-12 rounded-xl font-bold text-base bg-brand text-white hover:bg-brand/85 transition-colors flex items-center justify-center gap-2">
                        Listo
                    </button>
                </div>
            )}

            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                {/* AQUÍ IBA «perientreno 38/40P · 52/50H», y se ha ido (punto 110): «el peri
                    está en la lista de comidas». Tenía su propio contador porque los números
                    de arriba llevan el peri dentro y no se veía cuánto de eso era suyo; ahora
                    el intra y el post son dos filas más de la lista, con su objetivo al lado
                    y su estado, como cualquier comida. */}
                {/* «Ver detalle» y su tabla, fuera del teléfono. Lo que despliega es el
                    reparto del día comida a comida, y en móvil eso mismo está justo debajo:
                    la lista de comidas con su objetivo. Era la tercera vez que se decía lo
                    mismo en la misma pantalla. En escritorio se queda, donde la tabla se lee
                    de un vistazo y no obliga a bajar. */}
                <button onClick={() => setSummaryExpanded(!summaryExpanded)}
                    className="hidden lg:flex text-[11px] text-muted-foreground hover:text-foreground transition-colors items-center gap-1">
                    {summaryExpanded ? 'ocultar detalle' : 'ver detalle'}
                    {summaryExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>

                {/* Cómo va cada comida, de un vistazo y sin bajar: en la vista de todo
                    seguido es lo único que dice el estado del día sin recorrerlo entero. */}
                {/* Los puntos de C1 · Intra · C2... tampoco salen en el teléfono: ahí las
                    comidas están unas líneas más abajo, cada una con su estado y su nombre
                    completo, así que esto era el mismo dato dos veces y en abreviado. En
                    escritorio sí vale, porque con la vista de todo seguido es lo único que
                    dice cómo va el día sin recorrerlo entero. */}
                <span className="hidden lg:flex items-center gap-2.5 flex-wrap ml-auto">
                    {mealOrder.map((mealKey) => (
                        <span key={mealKey} className="flex items-center gap-1">
                            <StatusDot status={getMealStatus(mealKey)} />
                            <span className="text-[11px] text-muted-foreground">{mealInfo[mealKey].shortName}</span>
                        </span>
                    ))}
                </span>
            </div>

            {summaryExpanded && (
                <div className="mt-3 max-w-2xl hidden lg:block">
                    <DayDetailTable
                        mealOrder={mealOrder} mealInfo={mealInfo} calculateMealMacros={calculateMealMacros}
                        tipoDia={tipoDia} opcionPeri={opcionPeri}
                        mainP={mainP} mainH={mainH} mainG={mainG}
                        tgtP={tgtP} tgtH={tgtH} tgtG={tgtG}
                        totalPeriP={totalPeriP} totalPeriH={totalPeriH}
                    />
                </div>
            )}
        </section>
    );
};

export default DayHeader;
